import { useCallback, useEffect, useRef, useState } from 'react';

export const SELECTION_VISUAL_DURATION_MS = 10_000;
export const SELECTION_VISUAL_STEP_COUNT = 5;
const STEP_DURATION_MS = SELECTION_VISUAL_DURATION_MS / SELECTION_VISUAL_STEP_COUNT;
const FAST_COMPLETE_STEP_MS = 180;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useSelectionProgress = () => {
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(-1);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentStepRef = useRef(-1);

  const clearProgressTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  useEffect(() => clearProgressTimer, [clearProgressTimer]);

  const startProgress = useCallback(() => {
    clearProgressTimer();
    setRunning(true);
    setDone(false);
    setCurrentStep(0);
    currentStepRef.current = 0;

    intervalRef.current = setInterval(() => {
      setCurrentStep((step) => {
        const nextStep = Math.min(step + 1, SELECTION_VISUAL_STEP_COUNT - 1);
        currentStepRef.current = nextStep;
        if (nextStep === SELECTION_VISUAL_STEP_COUNT - 1) {
          clearProgressTimer();
        }
        return nextStep;
      });
    }, STEP_DURATION_MS);
  }, [clearProgressTimer]);

  const playUntilSelectionRequest = useCallback(async () => {
    if (currentStepRef.current < 0) {
      setCurrentStep(0);
      currentStepRef.current = 0;
    }
  }, []);

  const completeProgress = useCallback(async () => {
    clearProgressTimer();
    for (
      let step = currentStepRef.current + 1;
      step < SELECTION_VISUAL_STEP_COUNT;
      step += 1
    ) {
      setCurrentStep(step);
      currentStepRef.current = step;
      await wait(FAST_COMPLETE_STEP_MS);
    }
    setDone(true);
  }, [clearProgressTimer]);

  const resetProgress = useCallback(() => {
    clearProgressTimer();
    setCurrentStep(-1);
    currentStepRef.current = -1;
    setDone(false);
  }, [clearProgressTimer]);

  const stopProgress = useCallback(() => {
    clearProgressTimer();
    setRunning(false);
  }, [clearProgressTimer]);

  return {
    running,
    done,
    currentStep,
    startProgress,
    playUntilSelectionRequest,
    completeProgress,
    resetProgress,
    stopProgress,
  };
};
